import * as React from "react";

import { VizConfig } from "@/shared/config";
import { FileRecord } from "@/shared/domain/files";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";
import { SessionRecord } from "@/shared/domain/sessions";
import {
  TopicRecord,
  MessagePathRecord,
  MessagePathRepresentationMapping,
} from "@/shared/domain/topics";
import { State } from "@/shared/state/visualization";

import { MatchError } from "../layouts/LayoutToVizConfig";
import { Timer } from "../timer";

import { DEFAULT_TIME_DISPLAY_MODE, TimeDisplayMode } from "./TimeDisplayMode";

const noop = () => {};

export interface SetActiveLayoutOptions {
  skipReload?: boolean;
}

/**
 * A session loaded into the workspace: its record (name + `min_timestamp_ns`,
 * the sort key) and the live `FileRecord`s it contributed. Built by
 * `WorkspaceDataService` when it expands a session datasource, so it survives
 * save/share. A file shared by two sessions appears in each session's `files`.
 */
export interface Session {
  id: string;
  record: SessionRecord;
  files: FileRecord[];
}

export interface EphemeralWorkspaceState {
  // Server state
  files: FileRecord[];
  /**
   * The sessions loaded into the workspace, pre-sorted by
   * `record.min_timestamp_ns` (earliest first, `null`s last). Empty when no
   * session datasource was loaded; the Files tab then falls back to its
   * type-bucketed layout. The Files tab derives Ungrouped files (in no session)
   * from the leftover of `files`.
   */
  sessions: Session[];
  topics: TopicRecord[];
  messagePaths: MessagePathRecord[];
  /**
   * Each topic's message-path → representation mappings, computed once from
   * `topics`. Media panels use this to open their sources without a per-topic
   * representations request; keyed by topic id.
   */
  mappingsByTopicId: Map<string, MessagePathRepresentationMapping[]>;
  isDataLoading: boolean;
  error?: Error | null;
  layoutError?: Error | null;

  selectedPanelId: string | null;
  selectPanel: (panelId: string) => void;
  clearPanelSelection: () => void;
  /**
   * Clears the selection only when `panelId` is the one currently selected, so
   * that removing a panel the user has not selected leaves their selection
   * where it is.
   */
  clearPanelSelectionIfMatches: (panelId: string) => void;

  timer: Timer;

  // Hydration from a Layout or SharedWorkspace:
  isHydrating: boolean;
  setIsHydrating: (value: boolean) => void;

  // Active layout
  activeLayout: LayoutRecord;
  setActiveLayout: (
    layout: LayoutRecord,
    options?: SetActiveLayoutOptions,
  ) => void;
  skipLayoutRehydration: boolean;
  workspaceSnapshot: {
    panels: State["panels"];
    layout: State["layout"];
  };
  setWorkspaceSnapshot: (snapshot: {
    panels: State["panels"];
    layout: State["layout"];
  }) => void;

  // Keyed by panel id: the layout rules that bound to nothing in this
  // workspace when the layout was hydrated.
  dataMatchingErrors: Record<string, MatchError[]>;
  setDataMatchingErrors: (errors: Record<string, MatchError[]>) => void;

  timeDisplayMode: TimeDisplayMode;
  setTimeDisplayMode: (mode: TimeDisplayMode) => void;

  // Ephemeral panel state (viewport, filters, etc.)
  ephemeralPanelState: Map<string, Record<string, unknown>>;
  updateEphemeralPanelState: (
    panelId: string,
    updates: Record<string, unknown>,
  ) => void;
}

/**
 * Workspace state with nothing loaded and every setter a no-op. Components
 * rendered outside a Workspace provider read this through the context below;
 * tests override individual fields on top of it to build fakes.
 *
 * Each call builds new objects because the timer, layout, and maps are
 * mutable; one shared instance would let one caller's edits show up for
 * another.
 */
export function createDefaultEphemeralWorkspaceState(): EphemeralWorkspaceState {
  return {
    files: [],
    sessions: [],
    topics: [],
    messagePaths: [],
    mappingsByTopicId: new Map(),
    isDataLoading: false,
    error: null,
    layoutError: null,
    selectedPanelId: null,
    selectPanel: noop,
    clearPanelSelection: noop,
    clearPanelSelectionIfMatches: noop,
    timer: new Timer({ spans: [] }),
    // An unsaved placeholder carrying TEMPORARY_LAYOUT_ID, not a stored layout;
    // see that constant for how consumers present it.
    activeLayout: createEmptyLayout(),
    setActiveLayout: noop,
    skipLayoutRehydration: false,
    isHydrating: false,
    setIsHydrating: noop,
    dataMatchingErrors: {},
    setDataMatchingErrors: noop,
    workspaceSnapshot: {
      panels: VizConfig.default().panels,
      layout: VizConfig.default().layout,
    },
    setWorkspaceSnapshot: noop,
    timeDisplayMode: DEFAULT_TIME_DISPLAY_MODE,
    setTimeDisplayMode: noop,
    ephemeralPanelState: new Map(),
    updateEphemeralPanelState: noop,
  };
}

export const EphemeralWorkspaceStateContext =
  React.createContext<EphemeralWorkspaceState>(
    createDefaultEphemeralWorkspaceState(),
  );
